import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.graph');
import {
    AfterViewInit,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import cytoscape from 'cytoscape';
import { Service } from "src/libs/portal/season/service";

@Component({
    selector: 'wiz-page-graph',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.graph/view.scss */
:host {
  display: block;
  height: 100%;
}

:host ::ng-deep .cytoscape-container,
:host ::ng-deep canvas {
  outline: none;
}

@media (prefers-reduced-motion: reduce) {
  :host *,
  :host *::before,
  :host *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}`],
})
export class PageGraphComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('graphCanvas', { static: false })
    public graphCanvas?: ElementRef<HTMLDivElement>;

    public loading: boolean = false;
    public detailLoading: boolean = false;
    public error: any = null;
    public run: any = null;
    public runs: any[] = [];
    public selectedRunKey: string = '';
    public counts: any = {};
    public filters: any = { entity_types: [], predicates: [], assertions: [] };
    public provenance: any = {};
    public validation: any = {};

    public allNodes: any[] = [];
    public allTriples: any[] = [];
    public visibleNodes: any[] = [];
    public visibleTriples: any[] = [];
    public query: string = '';
    public entityTypeFilter: string = '';
    public predicateFilter: string = '';
    public assertionFilter: string = '';
    public includeIsolated: boolean = false;
    public viewMode: string = 'graph';

    public selectedKind: string = '';
    public selectedEntity: any = null;
    public selectedTriple: any = null;
    public filtersOpen: boolean = false;
    public detailOpen: boolean = false;

    private cy: any = null;
    private viewReady: boolean = false;
    private searchTimer: any = null;
    private resizeObserver: any = null;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        await this.service.auth.allow('/access');
        await this.load();
    }

    public ngAfterViewInit() {
        this.viewReady = true;
        this.scheduleGraph();
    }

    public ngOnDestroy() {
        if (this.searchTimer) clearTimeout(this.searchTimer);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.cy) this.cy.destroy();
        this.cy = null;
    }

    public async load() {
        if (this.loading) return;
        this.loading = true;
        this.error = null;
        await this.service.render();
        try {
            const identity = this.routeIdentity();
            const overview = await this.call('overview', identity);
            this.run = overview.run;
            this.selectedRunKey = this.run.document_key;
            this.counts = overview.counts || {};
            this.filters = overview.filters || {
                entity_types: [],
                predicates: [],
                assertions: [],
            };
            this.provenance = overview.provenance || {};
            this.validation = overview.validation || {};
            this.restoreFilterState();

            const [graph, runData] = await Promise.all([
                this.call('graph', {
                    ...this.identity(),
                    include_isolated: true,
                }),
                this.call('runs', {}),
            ]);
            this.allNodes = graph.nodes || [];
            this.allTriples = graph.triples || [];
            this.runs = runData.items || [];
            this.applyVisibleData();
        } catch (error) {
            this.applyError(error);
        } finally {
            this.loading = false;
            await this.service.render();
        }

        if (!this.error) {
            this.scheduleGraph(true);
            const route = this.routeIdentity();
            const tripleId = new URLSearchParams(location.search).get('triple');
            if (route.entity_id) {
                await this.openEntity(route.entity_id, false);
            } else if (tripleId) {
                await this.openTriple(tripleId, false);
            }
        }
    }

    public selectRun(documentKey: string) {
        this.selectedRunKey = documentKey;
        const selected = this.runs.find(
            (item: any) => item.document_key === documentKey
        );
        if (!selected || selected.document_key === this.run.document_key) return;
        location.href = `/graph/${encodeURIComponent(selected.document_id)}/${selected.version}`;
    }

    public runLabel(item: any) {
        if (!item) return '';
        return `v${item.version} · ${item.document_id} · ${this.formatNumber(item.triple_count)} Triple`;
    }

    public onSearchInput() {
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(async () => {
            await this.applyFilters();
        }, 300);
    }

    public async applyFilters() {
        this.applyVisibleData();
        this.updateLocation();
        await this.service.render();
        this.scheduleGraph(true);
    }

    public async resetFilters() {
        this.query = '';
        this.entityTypeFilter = '';
        this.predicateFilter = '';
        this.assertionFilter = '';
        this.includeIsolated = false;
        await this.applyFilters();
    }

    public async setViewMode(mode: string) {
        this.viewMode = mode;
        await this.service.render();
        if (mode === 'graph') this.scheduleGraph(false);
    }

    public searchResults() {
        const query = this.normalize(this.query);
        if (!query) return [];
        return this.allNodes
            .filter((node: any) => {
                if (
                    this.entityTypeFilter
                    && node.entity_type !== this.entityTypeFilter
                ) return false;
                return this.nodeMatchesQuery(node, query);
            })
            .sort((a: any, b: any) => {
                if (a.connected !== b.connected) return a.connected ? -1 : 1;
                if (a.degree !== b.degree) return b.degree - a.degree;
                return a.label.localeCompare(b.label, 'ko');
            })
            .slice(0, 8);
    }

    public async focusSearchResult(entityId: string) {
        const node = this.allNodes.find((item: any) => item.id === entityId);
        if (!node) return;
        if (!this.visibleNodes.some((item: any) => item.id === entityId)) {
            this.query = node.label;
            this.applyVisibleData();
            await this.service.render();
            this.scheduleGraph(true);
        }
        await this.openEntity(entityId);
        this.filtersOpen = false;
        await this.service.render();
    }

    public async openEntity(entityId: string, updateUrl: boolean = true) {
        if (!entityId) return;
        this.selectedKind = 'entity';
        this.selectedTriple = null;
        this.detailLoading = true;
        this.detailOpen = true;
        this.highlightEntity(entityId);
        if (updateUrl) this.updateLocation(entityId);
        await this.service.render();
        try {
            this.selectedEntity = await this.call('entity', {
                ...this.identity(),
                entity_id: entityId,
            });
        } catch (error) {
            this.applyError(error);
        } finally {
            this.detailLoading = false;
            await this.service.render();
            this.highlightEntity(entityId);
        }
    }

    public async openTriple(tripleId: string, updateUrl: boolean = true) {
        if (!tripleId) return;
        this.selectedKind = 'triple';
        this.selectedEntity = null;
        this.detailLoading = true;
        this.detailOpen = true;
        this.highlightTriple(tripleId);
        if (updateUrl) this.updateLocation('', tripleId);
        await this.service.render();
        try {
            this.selectedTriple = await this.call('triple', {
                ...this.identity(),
                triple_id: tripleId,
            });
        } catch (error) {
            this.applyError(error);
        } finally {
            this.detailLoading = false;
            await this.service.render();
            this.highlightTriple(tripleId);
        }
    }

    public async clearSelection() {
        this.selectedKind = '';
        this.selectedEntity = null;
        this.selectedTriple = null;
        this.detailOpen = false;
        if (this.cy) {
            this.cy.elements().removeClass('kg-faded kg-neighbor kg-focus');
            this.cy.elements().unselect();
        }
        this.updateLocation();
        await this.service.render();
    }

    public async showEntityNeighborhood() {
        if (!this.selectedEntity) return;
        const entityId = this.selectedEntity.entity.id;
        const relations = this.selectedEntity.relations || [];
        const relationIds = new Set(relations.map((item: any) => item.id));
        const nodeIds = new Set([entityId]);
        for (const relation of relations) {
            nodeIds.add(relation.neighbor.id);
        }
        this.visibleTriples = this.allTriples.filter(
            (item: any) => relationIds.has(item.id)
        );
        this.visibleNodes = this.allNodes.filter(
            (item: any) => nodeIds.has(item.id)
        );
        await this.service.render();
        this.renderGraph(true);
        this.highlightEntity(entityId);
    }

    public fitGraph() {
        if (this.cy && this.cy.elements().length) {
            this.cy.fit(this.cy.elements(), 48);
        }
    }

    public resetLayout() {
        this.renderGraph(true);
    }

    public async openFilters() {
        this.filtersOpen = true;
        await this.service.render();
    }

    public async closeFilters() {
        this.filtersOpen = false;
        await this.service.render();
    }

    public async closeDetail() {
        this.detailOpen = false;
        await this.service.render();
    }

    public async openDetail() {
        this.detailOpen = true;
        await this.service.render();
    }

    public openDocuments() {
        location.href = '/documents';
    }

    public openRelationReview() {
        if (!this.run) return;
        location.href = `/relation-review/${encodeURIComponent(this.run.document_id)}/${this.run.version}`;
    }

    public openReview(url: string) {
        if (url) this.service.href(url);
    }

    public formatNumber(value: any) {
        return Number(value || 0).toLocaleString('ko-KR');
    }

    public formatScore(value: any) {
        if (value === null || value === undefined) return '-';
        return Number(value).toFixed(2);
    }

    public typeLabel(type: string) {
        const labels: any = {
            PERSON: '인물',
            PLACE: '장소',
            ORGANIZATION: '기관',
            POLITY: '국가·정체',
            OFFICE: '관직',
            EVENT: '사건',
        };
        return labels[type] || type || '기타';
    }

    public typeClass(type: string) {
        const styles: any = {
            PERSON: 'border-indigo-200 bg-indigo-50 text-indigo-700',
            PLACE: 'border-emerald-200 bg-emerald-50 text-emerald-700',
            ORGANIZATION: 'border-amber-200 bg-amber-50 text-amber-800',
            POLITY: 'border-rose-200 bg-rose-50 text-rose-700',
            OFFICE: 'border-sky-200 bg-sky-50 text-sky-700',
            EVENT: 'border-violet-200 bg-violet-50 text-violet-700',
        };
        return styles[type] || 'border-zinc-200 bg-zinc-50 text-zinc-700';
    }

    public assertionClass(assertion: string) {
        const styles: any = {
            asserted: 'border-green-200 bg-green-50 text-green-700',
            negated: 'border-red-200 bg-red-50 text-red-700',
            speculative: 'border-amber-200 bg-amber-50 text-amber-800',
            reported: 'border-blue-200 bg-blue-50 text-blue-700',
            conditional: 'border-violet-200 bg-violet-50 text-violet-700',
        };
        return styles[assertion] || styles.asserted;
    }

    public visibleNodeLabel() {
        return `${this.formatNumber(this.visibleNodes.length)} / ${this.formatNumber(this.counts.node_count)}`;
    }

    public visibleTripleLabel() {
        return `${this.formatNumber(this.visibleTriples.length)} / ${this.formatNumber(this.counts.triple_count)}`;
    }

    public entityList() {
        return [...this.visibleNodes].sort((a: any, b: any) => {
            if (a.degree !== b.degree) return b.degree - a.degree;
            return a.label.localeCompare(b.label, 'ko');
        });
    }

    public tripleList() {
        const nodes = new Map(
            this.allNodes.map((item: any) => [item.id, item])
        );
        return this.visibleTriples.map((item: any) => ({
            ...item,
            source_label: nodes.get(item.source)?.label || item.source,
            target_label: nodes.get(item.target)?.label || item.target,
        }));
    }

    private applyVisibleData() {
        const query = this.normalize(this.query);
        const nodeById = new Map(
            this.allNodes.map((node: any) => [node.id, node])
        );
        const primaryIds = new Set<string>();

        for (const node of this.allNodes) {
            const typeMatch = !this.entityTypeFilter
                || node.entity_type === this.entityTypeFilter;
            const queryMatch = !query || this.nodeMatchesQuery(node, query);
            if (typeMatch && queryMatch) primaryIds.add(node.id);
        }

        const hasPrimaryFilter = !!this.entityTypeFilter || !!query;
        const triples = this.allTriples.filter((triple: any) => {
            if (
                this.predicateFilter
                && triple.predicate !== this.predicateFilter
            ) return false;
            if (
                this.assertionFilter
                && triple.assertion !== this.assertionFilter
            ) return false;
            if (
                hasPrimaryFilter
                && !primaryIds.has(triple.source)
                && !primaryIds.has(triple.target)
            ) return false;
            return true;
        });

        const visibleIds = new Set<string>();
        for (const triple of triples) {
            visibleIds.add(triple.source);
            visibleIds.add(triple.target);
        }
        if (this.includeIsolated || query) {
            for (const nodeId of primaryIds) visibleIds.add(nodeId);
        }

        this.visibleTriples = triples.filter(
            (triple: any) =>
                nodeById.has(triple.source) && nodeById.has(triple.target)
        );
        this.visibleNodes = this.allNodes.filter(
            (node: any) => visibleIds.has(node.id)
        );

        if (!this.includeIsolated && !hasPrimaryFilter) {
            this.visibleNodes = this.visibleNodes.filter(
                (node: any) => node.connected
            );
        }
        if (
            this.selectedEntity
            && !visibleIds.has(this.selectedEntity.entity.id)
        ) {
            this.selectedEntity = null;
            this.selectedKind = '';
        }
        if (
            this.selectedTriple
            && !this.visibleTriples.some(
                (item: any) => item.id === this.selectedTriple.triple.id
            )
        ) {
            this.selectedTriple = null;
            this.selectedKind = '';
        }
    }

    private scheduleGraph(rebuild: boolean = true) {
        if (!this.viewReady || this.viewMode !== 'graph' || this.loading) return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => this.renderGraph(rebuild));
        });
    }

    private renderGraph(rebuild: boolean = true) {
        const container = this.graphCanvas?.nativeElement;
        if (!container || this.viewMode !== 'graph') return;

        if (!this.cy) {
            this.cy = cytoscape({
                container: container,
                elements: [],
                minZoom: 0.2,
                maxZoom: 2.5,
                wheelSensitivity: 0.18,
                selectionType: 'single',
                boxSelectionEnabled: false,
                style: this.graphStyles() as any,
            });
            this.bindGraphEvents();
            if (typeof ResizeObserver !== 'undefined') {
                this.resizeObserver = new ResizeObserver(() => {
                    if (this.cy) this.cy.resize();
                });
                this.resizeObserver.observe(container);
            }
        }

        this.cy.elements().remove();
        this.cy.add(this.graphElements());
        if (!this.visibleNodes.length) return;

        if (rebuild) {
            const layout = this.cy.layout({
                name: 'cose',
                animate: false,
                fit: true,
                padding: 48,
                randomize: true,
                nodeRepulsion: () => 9000,
                idealEdgeLength: () => 95,
                edgeElasticity: () => 120,
                nestingFactor: 1.2,
                gravity: 0.35,
                numIter: 800,
            } as any);
            layout.run();
        } else {
            this.fitGraph();
        }

        if (this.selectedKind === 'entity' && this.selectedEntity) {
            this.highlightEntity(this.selectedEntity.entity.id);
        } else if (this.selectedKind === 'triple' && this.selectedTriple) {
            this.highlightTriple(this.selectedTriple.triple.id);
        }
    }

    private graphElements() {
        const nodes = this.visibleNodes.map((node: any) => ({
            group: 'nodes',
            data: {
                id: node.id,
                label: node.label,
                color: this.nodeColor(node.entity_type),
                shape: this.nodeShape(node.entity_type),
                size: Math.min(46, 27 + Math.sqrt(Math.max(1, node.degree)) * 4),
                type: node.entity_type,
            },
        }));
        const edges = this.visibleTriples.map((triple: any) => ({
            group: 'edges',
            data: {
                id: triple.id,
                source: triple.source,
                target: triple.target,
                label: triple.predicate_label,
                predicate: triple.predicate,
                assertion: triple.assertion,
                lineStyle: this.assertionLineStyle(triple.assertion),
            },
        }));
        return [...nodes, ...edges];
    }

    private graphStyles() {
        return [
            {
                selector: 'node',
                style: {
                    'background-color': 'data(color)',
                    'border-color': '#ffffff',
                    'border-width': 2,
                    'height': 'data(size)',
                    'width': 'data(size)',
                    'shape': 'data(shape)',
                    'label': 'data(label)',
                    'font-family': 'Pretendard, sans-serif',
                    'font-size': 9,
                    'font-weight': 600,
                    'color': '#27272a',
                    'text-wrap': 'ellipsis',
                    'text-max-width': 92,
                    'text-valign': 'bottom',
                    'text-margin-y': 7,
                    'text-background-color': '#ffffff',
                    'text-background-opacity': 0.88,
                    'text-background-padding': 2,
                    'overlay-opacity': 0,
                    'transition-property': 'opacity, border-width, border-color',
                    'transition-duration': '160ms',
                },
            },
            {
                selector: 'edge',
                style: {
                    'width': 1.4,
                    'line-color': '#a1a1aa',
                    'target-arrow-color': '#71717a',
                    'target-arrow-shape': 'triangle',
                    'arrow-scale': 0.8,
                    'curve-style': 'bezier',
                    'line-style': 'data(lineStyle)',
                    'label': 'data(label)',
                    'font-family': 'Pretendard, sans-serif',
                    'font-size': 8,
                    'font-weight': 600,
                    'color': '#52525b',
                    'text-rotation': 'autorotate',
                    'text-background-color': '#ffffff',
                    'text-background-opacity': 0.95,
                    'text-background-padding': 2,
                    'text-opacity': 0,
                    'overlay-opacity': 0,
                    'transition-property': 'opacity, width, line-color',
                    'transition-duration': '160ms',
                },
            },
            {
                selector: 'edge.kg-neighbor, edge:selected',
                style: {
                    'width': 2.6,
                    'line-color': '#4f46e5',
                    'target-arrow-color': '#4f46e5',
                    'text-opacity': 1,
                    'z-index': 20,
                },
            },
            {
                selector: 'node.kg-focus, node:selected',
                style: {
                    'border-color': '#4338ca',
                    'border-width': 4,
                    'z-index': 30,
                },
            },
            {
                selector: '.kg-faded',
                style: {
                    'opacity': 0.12,
                    'text-opacity': 0,
                },
            },
            {
                selector: 'edge.kg-hover',
                style: {
                    'text-opacity': 1,
                    'width': 2.2,
                    'line-color': '#6366f1',
                    'target-arrow-color': '#6366f1',
                },
            },
        ];
    }

    private bindGraphEvents() {
        this.cy.on('tap', 'node', async (event: any) => {
            await this.openEntity(event.target.id());
        });
        this.cy.on('tap', 'edge', async (event: any) => {
            await this.openTriple(event.target.id());
        });
        this.cy.on('tap', async (event: any) => {
            if (event.target === this.cy) await this.clearSelection();
        });
        this.cy.on('mouseover', 'edge', (event: any) => {
            event.target.addClass('kg-hover');
        });
        this.cy.on('mouseout', 'edge', (event: any) => {
            event.target.removeClass('kg-hover');
        });
    }

    private highlightEntity(entityId: string) {
        if (!this.cy) return;
        const node = this.cy.$id(entityId);
        if (!node.length) return;
        this.cy.elements().removeClass('kg-faded kg-neighbor kg-focus');
        const neighborhood = node.closedNeighborhood();
        this.cy.elements().not(neighborhood).addClass('kg-faded');
        neighborhood.edges().addClass('kg-neighbor');
        node.addClass('kg-focus').select();
        this.cy.animate({
            center: { eles: node },
            duration: 220,
        });
    }

    private highlightTriple(tripleId: string) {
        if (!this.cy) return;
        const edge = this.cy.$id(tripleId);
        if (!edge.length) return;
        this.cy.elements().removeClass('kg-faded kg-neighbor kg-focus');
        const focus = edge.union(edge.source()).union(edge.target());
        this.cy.elements().not(focus).addClass('kg-faded');
        edge.addClass('kg-neighbor').select();
        edge.source().addClass('kg-focus');
        edge.target().addClass('kg-focus');
        this.cy.animate({
            fit: { eles: focus, padding: 150 },
            duration: 220,
        });
    }

    private nodeMatchesQuery(node: any, query: string) {
        const material = [
            node.label,
            node.id,
            ...(node.aliases || []),
        ].join(' ').toLocaleLowerCase('ko');
        return material.indexOf(query) >= 0;
    }

    private normalize(value: string) {
        return String(value || '').trim().toLocaleLowerCase('ko');
    }

    private nodeColor(type: string) {
        const colors: any = {
            PERSON: '#6366f1',
            PLACE: '#10b981',
            ORGANIZATION: '#f59e0b',
            POLITY: '#f43f5e',
            OFFICE: '#0ea5e9',
            EVENT: '#8b5cf6',
        };
        return colors[type] || '#71717a';
    }

    private nodeShape(type: string) {
        const shapes: any = {
            PERSON: 'ellipse',
            PLACE: 'round-rectangle',
            ORGANIZATION: 'hexagon',
            POLITY: 'diamond',
            OFFICE: 'rectangle',
            EVENT: 'star',
        };
        return shapes[type] || 'ellipse';
    }

    private assertionLineStyle(assertion: string) {
        const styles: any = {
            asserted: 'solid',
            negated: 'dashed',
            speculative: 'dotted',
            reported: 'dashed',
            conditional: 'dotted',
        };
        return styles[assertion] || 'solid';
    }

    private identity() {
        return {
            document_id: this.run.document_id,
            version: this.run.version,
        };
    }

    private routeIdentity() {
        const parts = location.pathname.split('/').filter(
            (part: string) => !!part
        );
        const index = parts.indexOf('graph');
        if (index < 0 || !parts[index + 1]) return {};
        const rawVersion = parts[index + 2] || '';
        const identity: any = {
            document_id: decodeURIComponent(parts[index + 1]),
        };
        if (rawVersion) {
            identity.version = rawVersion.startsWith('v')
                ? rawVersion.slice(1)
                : rawVersion;
        }
        if (parts[index + 3]) {
            identity.entity_id = decodeURIComponent(parts[index + 3]);
        }
        return identity;
    }

    private restoreFilterState() {
        const params = new URLSearchParams(location.search);
        this.query = params.get('q') || '';
        this.entityTypeFilter = params.get('entityType') || '';
        this.predicateFilter = params.get('predicate') || '';
        this.assertionFilter = params.get('assertion') || '';
        this.includeIsolated = params.get('isolated') === 'true';
        this.viewMode = params.get('view') === 'list' ? 'list' : 'graph';
    }

    private updateLocation(
        entityId: string = '',
        tripleId: string = ''
    ) {
        if (!this.run) return;
        let path = `/graph/${encodeURIComponent(this.run.document_id)}/${this.run.version}`;
        const activeEntityId = entityId || (
            this.selectedKind === 'entity' && this.selectedEntity
                ? this.selectedEntity.entity.id
                : ''
        );
        if (activeEntityId) path += `/${encodeURIComponent(activeEntityId)}`;

        const params = new URLSearchParams();
        if (this.query.trim()) params.set('q', this.query.trim());
        if (this.entityTypeFilter) {
            params.set('entityType', this.entityTypeFilter);
        }
        if (this.predicateFilter) {
            params.set('predicate', this.predicateFilter);
        }
        if (this.assertionFilter) {
            params.set('assertion', this.assertionFilter);
        }
        if (this.includeIsolated) params.set('isolated', 'true');
        if (this.viewMode === 'list') params.set('view', 'list');
        const activeTripleId = tripleId || (
            this.selectedKind === 'triple' && this.selectedTriple
                ? this.selectedTriple.triple.id
                : ''
        );
        if (activeTripleId) params.set('triple', activeTripleId);
        const query = params.toString();
        history.replaceState({}, '', path + (query ? `?${query}` : ''));
    }

    private async call(name: string, payload: any) {
        const { code, data } = await wiz.call(name, payload || {});
        if (code !== 200) {
            const message = data && data.error && data.error.message
                ? data.error.message
                : '그래프 데이터를 불러오지 못했습니다.';
            throw new Error(message);
        }
        return data;
    }

    private applyError(error: any) {
        this.error = {
            message: error && error.message
                ? error.message
                : '그래프 데이터를 불러오지 못했습니다.',
        };
    }
}

export default PageGraphComponent;